import { useState, useEffect } from 'react';
import fireControllerService from '../services/fireControllerService';
import API_CONFIG from '../config/apiConfig';

/**
 * Converts an array of FireControllerStatusReport to MarkerData,
 * filtering out reports without coordinates.
 * @param {Array<Object>} reports - FireControllerStatusReport array
 * @returns {Array<{id: string, latitude: number, longitude: number, systemStatus: Object|null, streetAddr: string|null, hydroSystemStatus: Object|null}>}
 */
function reportsToMarkers(reports) {
  return reports
    .filter(r => r.latitude != null && r.longitude != null)
    .map(r => ({
      id: r.fireControllerId,
      latitude: r.latitude,
      longitude: r.longitude,
      systemStatus: r.hydroSystemStatus?.systemStatus ?? null,
      streetAddr: r.address?.streetAddr ?? null,
      hydroSystemStatus: r.hydroSystemStatus ?? null,
    }));
}

/**
 * Fetches and assembles marker data for the Location map on a recurring interval.
 * Admins receive all controllers via a single status-all call;
 * standard users receive their associated controllers via a single per-user status call.
 * @param {boolean} isAdmin - Whether the current user has admin role
 * @param {string} userId - The current user's ID (Keycloak sub)
 * @returns {Array<{id: string, latitude: number, longitude: number, systemStatus: Object|null, streetAddr: string|null}>}
 */
function useMarkerData(isAdmin, userId) {
  const [markerData, setMarkerData] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function fetchAdminMarkers() {
      const reports = await fireControllerService.getAllFireControllerStatuses();
      return reportsToMarkers(reports);
    }

    async function fetchUserMarkers() {
      const reports = await fireControllerService.getUserFireControllerStatuses(userId);
      return reportsToMarkers(reports);
    }

    async function load() {
      try {
        let markers;
        if (isAdmin) {
          // Admin path polls regardless of controller count so admins can detect when
          // controllers come online without a page refresh.
          markers = await fetchAdminMarkers();
        } else {
          if (!userId) return;
          markers = await fetchUserMarkers();
        }
        if (!cancelled) setMarkerData(markers);
      } catch (error) {
        console.error('Failed to load marker data:', error);
      }
    }

    load();

    const intervalId = setInterval(load, API_CONFIG.POLLING_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isAdmin, userId]);

  return markerData;
}

export default useMarkerData;
